import React from 'react';
import { Badge, Group, Image, Paper, Rating, Stack, ThemeIcon } from '@mantine/core';
import * as TablerIcons from '@tabler/icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import { textRoleCss } from '../../../puck/fields/TextRolesField';
import ProposalBoxAlign from '../../proposal/ProposalBoxAlign/ProposalBoxAlign';
import { ImageFrame } from '../../../media';
import { borderCssFromProps } from '../../../../shared/boxes/borderStyles';
import { boxShadowCss } from '../../../../shared/boxes/shadowStyles';
import { resolveInfoCardIllustration } from '../../../../shared/boxes/infoCard/infoCardLayout';
import {
  INFO_CARD_DESCRIPTION_STYLE,
  infoCardFlexAlign,
  infoCardIconSizePx,
  infoCardIllustrationSizePx,
  infoCardLineGap,
  infoCardLogoHeightPx,
  infoCardQuoteMarkSize,
  infoCardRatingColor,
  infoCardRatingSizePx,
  infoCardRatingStars,
  infoCardTextAlign,
  infoCardTitleTypography,
} from '../../../../shared/boxes/infoCard/infoCardRender';
import { editorImageSrc } from '../../../media/variableMedia';

/* Hors du composant : dans `InfoCard`, la prop `icon` (nom Tabler) masque le
   macro FontAwesome, et l'appel y lèverait « icon is not a function ». */
const QUOTE_MARK_ICON = icon({ name: 'quote-left', style: 'light' });

const DynamicIcon = ({ name, size = 28 }) => {
  const Icon = TablerIcons[name] || TablerIcons.IconStar;
  return <Icon size={size} stroke={1.5} />;
};

/**
 * Bloc Puck — Carte info : illustration (icône OU photo) + valeur (pas
 * forcément numérique) + description + badges. Box unitaire des
 * « Chiffres clés » et de l'« Équipe » (une par colonne de section, règle
 * 1 box par colonne).
 */
/* Typographies, tailles et alignements vivent dans
   shared/boxes/infoCard/infoCardRender.js : la box du document rend la même
   carte, elle doit décider avec les mêmes valeurs. */
const DESCRIPTION_STYLE = {
  color: 'var(--mantine-color-dimmed)', ...INFO_CARD_DESCRIPTION_STYLE,
};

/** Cadre réservé de la photo : visible en édition seulement. */
const ImagePlaceholder = ({ size, round }) => (
  <div
    title="Espace réservé — aucune photo choisie, rien ne s'affiche sur le site."
    style={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: round ? '50%' : 'var(--mantine-radius-md)',
      border: '1px dashed var(--mantine-color-gray-4)',
      color: 'var(--mantine-color-dimmed)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <FontAwesomeIcon
      icon={icon({ name: 'image', style: 'light' })}
      style={{ fontSize: Math.round(size * 0.4) }}
    />
  </div>
);

const InfoCard = ({
  id,
  /** 'icon' | 'image' | 'none' — ce qui illustre la carte. */
  illustration = 'icon',
  icon = '',
  /** Photo (mode illustration « image »). */
  imageUrl = '',
  imageUrlFocalPoint,
  imageUrlVariable = null,
  imageUrlForm = null,
  value = '',
  description = '',
  /** Typographie de la ligne du haut — cf. INFO_CARD_TITLE_TYPOGRAPHY. */
  titleStyle = 'figure',
  /** Guillemet décoratif au-dessus du titre, teinté par `color`. */
  quoteMark = false,
  /** Note sur 5 sous la description — 0 = rien de rendu. */
  rating = 0,
  /** Couleur des étoiles — vide = celle de l'illustration. */
  ratingColor = '',
  /** Taille d'une étoile (longueur CSS, champ px↔rem) — 16 px par défaut. */
  ratingSize = '1rem',
  /** Logo de l'entreprise citée, sous les badges (jamais recadré). */
  logoUrl = '',
  logoUrlVariable = null,
  logoUrlForm = null,
  logoHeight = '2rem',
  /** Badges optionnels sous la description : `{ label, color, variant }`. */
  badges = [],
  /** Couleur CSS (color picker) de l'icône. Vide = bleu Mantine. */
  color = '',
  /** Bordures configurables — cf. utils/borderStyles (défaut : 4 côtés, arrondi). */
  border = null,
  contentAlign = 'center',
  /** 'vertical' (illustration au-dessus) | 'horizontal' (illustration à gauche). */
  cardLayout = 'vertical',
  /** Espace vertical entre illustration / valeur / description — longueur CSS
   *  (rem, champ px↔rem) ; legacy number = px. Vide = 0. */
  lineGap = '0.625rem',
  /** 'round' | 'square' — forme de l'icône comme de la photo. */
  illustrationShape = 'round',
  /** Hauteur de l'illustration (longueur CSS, champ px↔rem) — 52 px par défaut. */
  illustrationSize = '3.25rem',
  /** Couleur de fond CSS de la card — vide = fond Paper par défaut. */
  backgroundColor = '',
  /* Clé de l'échelle partagée ; 'none' pour une carte déjà en base, qui doit
     rendre exactement comme avant. */
  shadow = 'none',
  /** Position de la card dans sa colonne (comme l'Image). */
  alignHorizontal = 'center',
  alignVertical = 'center',
  /** Style par défaut des textes, par rôle (`value`, `description`). */
  textStyles = null,
  /** Marge intérieure (longueur CSS) — vide = 32 px, celle d'avant le réglage. */
  padding = '',
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const photoUrl = editorImageSrc({ imageUrl, imageUrlVariable, imageUrlForm }, 'imageUrl', { isEditing });
  const companyLogoUrl = editorImageSrc({ logoUrl, logoUrlVariable, logoUrlForm }, 'logoUrl', { isEditing });
  const sizePx = infoCardIllustrationSizePx(illustrationSize);
  const round = illustrationShape !== 'square';
  const kind = resolveInfoCardIllustration({
    illustration, icon, imageUrl: photoUrl, isEditing,
  });
  const horizontal = cardLayout === 'horizontal';

  let illustrationNode = null;
  if (kind === 'icon') {
    illustrationNode = (
      <ThemeIcon
        size={sizePx}
        radius={round ? 'xl' : 'md'}
        color={color || 'blue'}
        variant="light"
      >
        <DynamicIcon name={icon} size={infoCardIconSizePx(illustrationSize)} />
      </ThemeIcon>
    );
  } else if (kind === 'image') {
    illustrationNode = (
      <ImageFrame
        src={photoUrl}
        mediaFocalPoint={imageUrlFocalPoint}
        alt=""
        mediaAspectRatio="1/1"
        fallbackRatio={1}
        radius={round ? 'xl' : 'md'}
        isEditing={isEditing}
        style={{ width: sizePx, flexShrink: 0 }}
      />
    );
  } else if (kind === 'placeholder') {
    illustrationNode = <ImagePlaceholder size={sizePx} round={round} />;
  }

  // Badges : attributs atomiques stylés par Mantine, réglés en sidebar
  // (libellé, couleur, style) — cf. InfoCardBadgesField.
  const badgeNode = badges.length > 0 && (
    <Group gap="xs" wrap="wrap" justify={infoCardFlexAlign(contentAlign)}>
      {badges.map((badge, idx) => (
        <Badge
          key={idx}
          size="sm"
          /* Vide = couleur principale du thème de la proposal. */
          color={badge.color || undefined}
          variant={badge.variant || 'light'}
        >
          {badge.label}
        </Badge>
      ))}
    </Group>
  );

  const stars = infoCardRatingStars(rating);
  const ratingNode = stars > 0 && (
    <Rating
      value={stars}
      count={5}
      readOnly
      size={infoCardRatingSizePx(ratingSize)}
      // Couleur propre aux étoiles : sans elle, teinter l'illustration
      // teintait aussi la note, ce qui n'a pas de raison d'être.
      color={infoCardRatingColor({ ratingColor, color })}
    />
  );

  // Logo de l'entreprise citée : jamais recadré, jamais de fond — même
  // traitement que les logos du bandeau client et du pied de page.
  const logoNode = companyLogoUrl ? (
    <Image
      src={companyLogoUrl}
      alt=""
      h={infoCardLogoHeightPx(logoHeight)}
      w="auto"
      fit="contain"
    />
  ) : null;

  const quoteNode = quoteMark && (
    <FontAwesomeIcon
      icon={QUOTE_MARK_ICON}
      style={{
        fontSize: infoCardQuoteMarkSize(titleStyle),
        /* Vide = couleur principale du thème de la proposal. */
        color: color || 'var(--mantine-primary-color-filled)',
        opacity: 0.35,
        flexShrink: 0,
      }}
    />
  );

  const texts = (
    <Stack align={infoCardFlexAlign(contentAlign)} gap={infoCardLineGap(lineGap)}>
      {quoteNode}
      <PuckInlineRichText
        id={id}
        puck={puck}
        html={value}
        contentProp="value"
        style={{ ...infoCardTitleTypography(titleStyle), width: '100%', ...textRoleCss(textStyles, 'value') }}
        minHeight={isEditing ? 32 : undefined}
        placeholder="Titre"
      />
      <PuckInlineRichText
        id={id}
        puck={puck}
        html={description}
        contentProp="description"
        style={{ ...DESCRIPTION_STYLE, ...textRoleCss(textStyles, 'description') }}
        minHeight={isEditing ? 24 : undefined}
        placeholder="Description"
      />
      {ratingNode}
      {badgeNode}
      {logoNode}
    </Stack>
  );

  return (
    <ProposalBoxAlign horizontal={alignHorizontal} vertical={alignVertical} innerMode="full">
      <Paper
        p={padding ? undefined : 'xl'}
        style={{
          ...(padding ? { padding } : {}),
          textAlign: infoCardTextAlign(contentAlign),
          ...borderCssFromProps(border),
          ...(backgroundColor ? { backgroundColor } : {}),
          ...(boxShadowCss(shadow) ? { boxShadow: boxShadowCss(shadow) } : {}),
        }}
      >
        {/* « Horizontale » tient sa promesse : illustration à gauche, textes
            à droite ; « Verticale » empile tout dans la même colonne. */}
        {horizontal ? (
          <Group align="center" gap="md" wrap="nowrap">
            {illustrationNode}
            <div style={{ flex: 1, minWidth: 0 }}>{texts}</div>
          </Group>
        ) : (
          <Stack align={infoCardFlexAlign(contentAlign)} gap={infoCardLineGap(lineGap)}>
            {illustrationNode}
            {texts}
          </Stack>
        )}
      </Paper>
    </ProposalBoxAlign>
  );
};

export default InfoCard;
